import React, { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Plus, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';

// How likely a member is the payer, judged from the bank narrative (last name, full name, email local-part, first name).
export function memberLikelihood(description, m) {
  const d = (description || '').toLowerCase();
  if (!d) return 0;
  const words = d.split(/[^a-z0-9]+/).filter((w) => w.length >= 3);
  const fn = (m.first_name || (m.name || '').split(' ')[0] || '').toLowerCase().trim();
  const ln = (m.last_name || (m.name || '').split(' ').slice(-1)[0] || '').toLowerCase().trim();
  const name = (m.name || '').toLowerCase().trim();
  const local = ((m.email || '').split('@')[0] || '').toLowerCase();
  let s = 0;
  if (name.length >= 4 && d.includes(name)) s += 100;
  if (ln.length >= 3 && words.includes(ln)) s += 60;
  else if (ln.length >= 3 && d.includes(ln)) s += 40;
  if (fn.length >= 3 && words.includes(fn)) s += 25;
  else if (fn && d.includes(fn)) s += 10;
  if (local.length >= 4 && d.includes(local)) s += 50;
  return s;
}

const btnCls = 'inline-flex items-center justify-between gap-1 h-8 px-2 rounded border text-xs bg-white w-full text-left';
const bd = { borderColor: 'rgba(0,0,0,0.2)' };

// Member picker: most likely payers first, progressive search as you type.
export function MemberCombobox({ members, value, onChange, description = '', suggestedId = '', testid, placeholder = 'Choose member…' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ranked = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = members
      .filter((m) => !needle || `${m.name || ''} ${m.email || ''} ${m.first_name || ''} ${m.last_name || ''}`.toLowerCase().includes(needle))
      .map((m) => ({ m, score: (m.user_id === suggestedId ? 200 : 0) + memberLikelihood(description, m) }))
      .sort((a, b) => b.score - a.score || (a.m.name || '').localeCompare(b.m.name || ''));
    return list.slice(0, 60);
  }, [members, q, description, suggestedId]);
  const current = members.find((m) => m.user_id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-expanded={open} className={btnCls} style={{ ...bd, minWidth: '14rem' }} data-testid={testid}>
          <span className="truncate">{current ? current.name : <span style={{ color: 'var(--olive)' }}>{placeholder}</span>}</span>
          <ChevronsUpDown size={13} className="shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-80" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type a name or email…" value={q} onValueChange={setQ} data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList className="max-h-72">
            <CommandEmpty>No member matches.</CommandEmpty>
            <CommandGroup heading={q ? 'Matches' : 'Most likely first'}>
              {ranked.map(({ m, score }) => (
                <CommandItem key={m.user_id} value={m.user_id} onSelect={() => { onChange(m.user_id); setOpen(false); setQ(''); }} data-testid={testid ? `${testid}-opt-${m.user_id}` : undefined}>
                  <Check size={13} className={`mr-2 ${value === m.user_id ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="flex-1 truncate">{m.name}<span className="ml-1 text-[11px]" style={{ color: 'var(--olive)' }}>{m.email}</span></span>
                  {score > 0 && <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] px-1.5 rounded-full" style={{ background: score >= 100 ? '#e6f4ea' : 'rgba(201,161,59,0.2)', color: score >= 100 ? '#1a7f37' : '#7a5b00' }} title="Likely match from the bank narrative"><Sparkles size={9} /> likely</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Category picker: existing categories in a searchable dropdown, with "Add new" for anything not yet in the list.
export function CategoryCombobox({ categories, value, onChange, testid, width = 'w-40' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const needle = q.trim();
  const shown = categories.filter((c) => !needle || c.toLowerCase().includes(needle.toLowerCase()));
  const canAdd = needle && !categories.some((c) => c.toLowerCase() === needle.toLowerCase());
  const pick = (c) => { onChange(c); setOpen(false); setQ(''); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-expanded={open} className={`${btnCls} ${width}`} style={bd} data-testid={testid}>
          <span className="truncate">{value || <span style={{ color: 'var(--olive)' }}>Category…</span>}</span>
          <ChevronsUpDown size={13} className="shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search or type a new category…" value={q} onValueChange={setQ} data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList className="max-h-64">
            {!shown.length && !canAdd && <CommandEmpty>No categories yet — type one to add it.</CommandEmpty>}
            {canAdd && (
              <CommandGroup heading="New">
                <CommandItem value={`__add__${needle}`} onSelect={() => pick(needle)} data-testid={testid ? `${testid}-add` : undefined}><Plus size={13} className="mr-2" /> Add “{needle}”</CommandItem>
              </CommandGroup>
            )}
            <CommandGroup heading="Categories">
              {value && <CommandItem value="__clear__" onSelect={() => pick('')} className="italic" style={{ color: 'var(--olive)' }}>(no category)</CommandItem>}
              {shown.map((c) => (
                <CommandItem key={c} value={c} onSelect={() => pick(c)} data-testid={testid ? `${testid}-opt-${c.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}` : undefined}>
                  <Check size={13} className={`mr-2 ${value === c ? 'opacity-100' : 'opacity-0'}`} /> {c}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Bank narrative that can be opened to read in full.
export function DescriptionCell({ text, badge, testid }) {
  const [open, setOpen] = useState(false);
  const long = (text || '').length > 42;
  return (
    <div className={open ? 'whitespace-pre-wrap break-words' : 'truncate'} style={{ maxWidth: open ? '32rem' : '280px' }} title={open ? '' : text} data-testid={testid}>
      {badge}
      <span onClick={() => long && setOpen((o) => !o)} className={long ? 'cursor-pointer' : ''} role={long ? 'button' : undefined} data-testid={testid ? `${testid}-text` : undefined}>{text}</span>
      {long && <button type="button" onClick={() => setOpen((o) => !o)} className="ml-1 text-[11px] underline" style={{ color: 'var(--navy)' }} data-testid={testid ? `${testid}-toggle` : undefined}>{open ? 'less' : 'more'}</button>}
    </div>
  );
}

// Matched-member cell for ledgers: shows the current match, lets you pick/change a member (likely payers first) or unmatch.
export function MemberMatchCell({ row, members, onChanged, testid }) {
  const { http } = useAuth();
  const [busy, setBusy] = useState(false);
  const change = async (memberId) => {
    if (!memberId || memberId === row.matched_member_id) return;
    setBusy(true);
    try {
      await http.post(`/admin/basiq/transactions/${row.basiq_id}/match`, { member_id: memberId, record_payment: row.direction === 'credit' });
      toast.success('Matched to member.'); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not match.'); }
    setBusy(false);
  };
  const unmatch = async () => {
    setBusy(true);
    try { await http.post(`/admin/basiq/transactions/${row.basiq_id}/unmatch`); toast.success('Match removed.'); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not unmatch.'); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      <div className="w-44"><MemberCombobox members={members} value={row.matched_member_id || ''} onChange={change} description={row.description} suggestedId={row.suggested_member_id || ''} testid={testid ? `${testid}-pick` : undefined} placeholder="Match member…" /></div>
      {row.matched_member_id && <button type="button" onClick={unmatch} disabled={busy} title="Remove match" className="text-[11px] underline" style={{ color: '#8a1f11' }} data-testid={testid ? `${testid}-unmatch` : undefined}>unmatch</button>}
    </div>
  );
}
